// Reviewer guidance for telecom_v2 Infrastructure Security controls.
// Informational only — does not affect scoring.
// NIST CSF mappings are lightweight (1–2 per control) and not interactive.

export interface InfrastructureControlGuidance {
  description: string;
  why: string;
  goodLooksLike: string[];
  evidence: string[];
  nistMappings: string[]; // e.g. ["PR.AA-06", "PR.IR-02"]
}

export const INFRASTRUCTURE_CONTROL_GUIDANCE: Record<string, InfrastructureControlGuidance> = {
  facility_access_controls: {
    description: "Physical barriers and access management at facility entry points protecting telecom infrastructure.",
    why: "Unauthorized physical access to telecom facilities can compromise network integrity, customer data, and service availability.",
    goodLooksLike: [
      "Multi-factor physical access (badge + PIN/biometric) at perimeter and sensitive zones.",
      "Documented access control policy with periodic access reviews.",
      "Tailgating prevention measures (mantraps, turnstiles).",
    ],
    evidence: [
      "Access control policy document.",
      "Recent access review report.",
      "Photos / floor plans of entry controls.",
    ],
    nistMappings: ["PR.AA-06", "PR.IR-02"],
  },
  visitor_management: {
    description: "Process for identifying, authorizing, escorting, and logging non-employee facility visitors.",
    why: "Unmanaged visitors create insider-threat exposure and audit gaps for regulated telecom environments.",
    goodLooksLike: [
      "Pre-registration and ID verification for all visitors.",
      "Escort requirement in restricted areas.",
      "Retained visitor logs (typically 12+ months).",
    ],
    evidence: [
      "Visitor management procedure.",
      "Sample visitor log extract (redacted).",
      "Visitor badge sample.",
    ],
    nistMappings: ["PR.AA-06"],
  },
  badge_biometric_access: {
    description: "Electronic credentialing for personnel access to facilities and sensitive zones.",
    why: "Strong authentication of personnel at the physical layer prevents credential sharing and supports forensic traceability.",
    goodLooksLike: [
      "Unique badges per person; no shared credentials.",
      "Biometric or MFA for high-sensitivity zones (data halls, MDF/IDF rooms).",
      "Automated revocation on offboarding.",
    ],
    evidence: [
      "Badge issuance / revocation procedure.",
      "Sample audit log of zone entries.",
      "HR-to-access deprovisioning workflow.",
    ],
    nistMappings: ["PR.AA-01", "PR.AA-06"],
  },
  cctv_coverage: {
    description: "Video surveillance of facility perimeters, entry points, and critical infrastructure areas.",
    why: "Surveillance deters unauthorized activity and provides forensic evidence following physical incidents.",
    goodLooksLike: [
      "Coverage of all entry/exit points and critical equipment rooms.",
      "Retention aligned to regulatory requirement (typically 30–90 days).",
      "Tamper alerts and periodic camera health checks.",
    ],
    evidence: [
      "Camera coverage map.",
      "Retention policy.",
      "Recent footage review log.",
    ],
    nistMappings: ["DE.CM-02", "PR.IR-02"],
  },
  rack_cage_security: {
    description: "Physical isolation of telecom equipment within locked racks or caged enclosures.",
    why: "Rack-level controls limit blast radius of an intrusion and segregate customer or function-specific equipment.",
    goodLooksLike: [
      "Locked racks/cages with key or smart-lock control.",
      "Access logs at rack/cage level for sensitive systems.",
      "Tamper-evident seals on critical equipment.",
    ],
    evidence: [
      "Rack/cage access policy.",
      "Photos of locked enclosures.",
      "Smart-lock audit log sample.",
    ],
    nistMappings: ["PR.IR-02"],
  },
  fire_suppression: {
    description: "Detection and suppression systems protecting equipment areas from fire damage.",
    why: "Fire events can cause prolonged service outages and total loss of network equipment.",
    goodLooksLike: [
      "Clean-agent (e.g., FM-200, NOVEC) suppression in equipment rooms.",
      "Annual inspection and certification.",
      "Integrated fire alarm monitored 24/7.",
    ],
    evidence: [
      "Most recent inspection certificate.",
      "Suppression system schematic.",
      "Alarm test records.",
    ],
    nistMappings: ["PR.IR-04"],
  },
  hvac_environmental: {
    description: "Climate control and environmental monitoring for equipment rooms.",
    why: "Out-of-spec temperature or humidity causes equipment failure, reduces lifespan, and triggers thermal shutdowns.",
    goodLooksLike: [
      "Redundant (N+1) HVAC units.",
      "Continuous temperature/humidity monitoring with alerting.",
      "Documented operating envelope per ASHRAE guidance.",
    ],
    evidence: [
      "HVAC maintenance log.",
      "Environmental monitoring dashboard screenshot.",
      "Recent alert/incident records.",
    ],
    nistMappings: ["PR.IR-04", "DE.CM-02"],
  },
  ups_generator: {
    description: "Backup power systems sustaining operations during utility outages.",
    why: "Telecom service continuity depends on uninterrupted power; outages directly translate to customer impact.",
    goodLooksLike: [
      "UPS sized for full graceful generator handoff.",
      "Generator with on-site fuel for documented runtime (e.g., 48–72 hours).",
      "Monthly load tests and annual full transfer tests.",
    ],
    evidence: [
      "UPS/generator test logs.",
      "Fuel contract / refueling SLA.",
      "Load capacity calculations.",
    ],
    nistMappings: ["PR.IR-04", "RC.RP-01"],
  },
  network_room_protection: {
    description: "Enhanced controls protecting MDF/IDF rooms, core routers, and switching equipment.",
    why: "Network rooms are single points of failure; physical compromise can disrupt or intercept all traffic.",
    goodLooksLike: [
      "Restricted-access room with separate credential set.",
      "Environmental and intrusion sensors.",
      "Documented entry log with reason-for-access.",
    ],
    evidence: [
      "Network room access procedure.",
      "Sensor configuration export.",
      "Sample entry log.",
    ],
    nistMappings: ["PR.IR-02", "PR.AA-06"],
  },
  cable_pathway_security: {
    description: "Protection of cable runs, conduits, risers, and entry facilities from tampering or damage.",
    why: "Exposed pathways enable wiretapping, accidental cuts, or targeted sabotage of communications links.",
    goodLooksLike: [
      "Conduits or armored pathways for sensitive runs.",
      "Locked manholes / vaults at key handoff points.",
      "Diverse entry paths for redundant circuits.",
    ],
    evidence: [
      "Pathway diagram.",
      "Inspection records of vaults/manholes.",
      "Diversity attestation from carrier.",
    ],
    nistMappings: ["PR.IR-02"],
  },
  ddos_edge_protection: {
    description: "Network edge defenses against volumetric and application-layer denial-of-service attacks.",
    why: "DDoS is a primary availability threat for telecom operators and can cascade across customer services.",
    goodLooksLike: [
      "Always-on scrubbing or upstream provider mitigation.",
      "Documented thresholds and runbooks.",
      "Periodic mitigation drills.",
    ],
    evidence: [
      "DDoS mitigation contract / configuration.",
      "Recent mitigation event report.",
      "Drill or tabletop exercise notes.",
    ],
    nistMappings: ["PR.IR-03", "DE.CM-01"],
  },
  site_incident_response: {
    description: "Documented procedures for responding to physical, environmental, or facility-related incidents.",
    why: "A coordinated site response limits impact, accelerates recovery, and supports regulatory reporting obligations.",
    goodLooksLike: [
      "Site-specific runbooks (fire, power loss, intrusion, environmental).",
      "Defined on-call escalation chain.",
      "Post-incident review process.",
    ],
    evidence: [
      "Incident response playbook.",
      "On-call roster.",
      "Recent post-incident review.",
    ],
    nistMappings: ["RS.MA-01", "RC.RP-01"],
  },
};
